import { cellAt, frameOutline, sourceKindOf } from '@signa/renderer';
import {
  cellCountOf,
  cellKey,
  pickCell,
  slotCountOf,
  type CellRef,
  type FrameLike,
  type NormalizedPoint,
  type PublicVideoFrame,
  type SourceKind,
} from '@signa/shared';

/**
 * 素材を割り当てる公開 API(`SignaPreview.assign` / `clear`)の判定(#284)。
 *
 * 正本は [ADR-0048](../../../../docs/adr/0048-embed-assign-api.md)、
 * 仕様は [#33 の DESIGN.md](../../../../docs/issues/33-viewer-embed/DESIGN.md)。
 *
 * **判定をシェルから分けている**のは、DOM を用意せずテストできるようにするため
 * ([ADR-0019](../../../../docs/adr/0019-dev-foundation.md) が jsdom を導入しない方針)。
 */

/**
 * `assign` / `clear` の失敗のコード。**`v1` の契約**(ADR-0048 —— 増やしてよいが、意味を変える・消すのは版を上げるとき)。
 *
 * テンプレートの読み込みに失敗していたときは、そのコード(`LINK_EXPIRED` など —— `VIEWER_ERROR_CODES`)で reject する。
 */
export const ASSIGN_ERROR_CODES = {
  /** テンプレートの読み込み前・その要素が初期化されていない・破棄された。**`signa:ready` の後に呼ぶ。** */
  NOT_READY: 'NOT_READY',
  /** 外部キー / 枠 / セルが見つからない。 */
  FRAME_NOT_FOUND: 'FRAME_NOT_FOUND',
  /** 外部キーが 2 つ以上のセルにある。**どこにも入れない**(間違った面に映るのを防ぐ —— 同意書 Q2)。 */
  DUPLICATE_KEY: 'DUPLICATE_KEY',
  /** `File` / `Blob` が再生・表示できない。 */
  VIDEO_UNSUPPORTED: 'VIDEO_UNSUPPORTED',
  /** GPU のテクスチャの上限を超える解像度(**ドロップの `signa:error` は `VIDEO_UNSUPPORTED` のまま** —— 同意書 Q4)。 */
  SOURCE_TOO_LARGE: 'SOURCE_TOO_LARGE',
  /** URL から読めない。**通信・CORS・形式を区別しない**(ADR-0048 の決定 5)。 */
  SOURCE_LOAD_FAILED: 'SOURCE_LOAD_FAILED',
  /** WebGL2 が使えない(#283 の C —— 案内は埋め込み先が出す)。 */
  UNSUPPORTED: 'UNSUPPORTED',
  /** 書き出しの最中。素材の要素を直接動かしているので、途中で差し替えると壊れる。 */
  BUSY: 'BUSY',
  /** 同じセルに後から `assign` / `clear` が来た。**後に呼んだほうが勝つ**(ADR-0048 の決定 2)。 */
  SUPERSEDED: 'SUPERSEDED',
} as const;

export type AssignErrorCode = (typeof ASSIGN_ERROR_CODES)[keyof typeof ASSIGN_ERROR_CODES];

/**
 * `assign` / `clear` の reject の値。**`catch (e) { e.code }` で読める**(ADR-0048 の決定 1)。
 *
 * `code` は `AssignErrorCode` か、テンプレートの読み込みに失敗したときのコード。
 */
export class SignaPreviewError extends Error {
  constructor(readonly code: string) {
    super(`[SignaPreview] ${code}`);
    this.name = 'SignaPreviewError';
  }
}

/** 入れる先。**連携先は常に `externalKey` を指定する**(全画面用と分割用の枠が重なるため)。 */
export type AssignTarget =
  { readonly externalKey: string } | { readonly frameId: string; readonly cellIndex?: number };

/** 素材。`File` は `Blob` の一種。 */
export type AssignSource = Blob | { readonly url: string; readonly kind?: SourceKind };

type Resolved<T> =
  { readonly ok: true; readonly value: T } | { readonly ok: false; readonly code: AssignErrorCode };

const notFound = { ok: false, code: ASSIGN_ERROR_CODES.FRAME_NOT_FOUND } as const;

/** 外部キーでセルを引く。**0 件は `FRAME_NOT_FOUND`、2 件以上は `DUPLICATE_KEY`。** */
export function findByExternalKey(
  frames: readonly PublicVideoFrame[],
  externalKey: string,
): Resolved<CellRef> {
  const hits: CellRef[] = [];
  for (const frame of frames) {
    for (const cell of frame.cells ?? []) {
      if (cell.externalKey === externalKey)
        hits.push({ frameId: frame.id, cellIndex: cell.cellIndex });
    }
  }
  const [only] = hits;
  if (only === undefined) return notFound;
  if (hits.length > 1) return { ok: false, code: ASSIGN_ERROR_CODES.DUPLICATE_KEY };
  return { ok: true, value: only };
}

/** `cellIndex` が枠のセルの範囲にあるか。**整数だけ**(`1.5` や `NaN` は範囲外)。 */
function inRange(frame: PublicVideoFrame, cellIndex: number): boolean {
  return Number.isInteger(cellIndex) && cellIndex >= 0 && cellIndex < cellCountOf(frame.grid);
}

/**
 * **ドロップ先のセル**(シェルの `pick`)。**表示と実際の割り当てで同じものを使う。**
 *
 * どのセルかは描画コアの [`cellAt`](../../../renderer/src/hit-test.ts) に委ねる —— 盤面は台形なので、
 * 逆写像なしには決められない(ADR-0029)。内外の判定は**描画した形**([`frameOutline`](../../../renderer/src/folds.ts))で
 * 行う —— 折れた枠の角は 4 隅の線から張り出す・へこむ(#339)、曲面は見えている部分だけ(#361)。
 *
 * **曲面の枠は保存した形で判定する**(#361 / ADR-0060)。正規化座標のまま渡す(描画コアがそう約束している)。
 * [`pickCell`](../../../shared/src/quad.ts) は曲面の枠の輪郭を見ず `cellAt` だけで決め、`null`(手前の裏に隠れた・裏に回った)なら当たらない扱いにする。
 */
export function pickDropCell(
  point: NormalizedPoint | null,
  frames: readonly FrameLike[],
  assigned: ReadonlySet<string>,
): CellRef | null {
  return pickCell(
    point,
    frames,
    assigned,
    (frame, at) => cellAt(at, frame.quad, frame.grid, frame.folds, frame.curve),
    (frame) => frameOutline(frame.quad, frame.grid, frame.folds, frame.curve),
  );
}

/**
 * `assign` の入れる先(連動先を引く前の 1 セル)。
 *
 * - `{ externalKey }` —— [`findByExternalKey`](#findByExternalKey)
 * - `{ frameId, cellIndex }` —— その枠のそのセル。枠が無い・範囲外なら `FRAME_NOT_FOUND`
 * - `{ frameId }` —— **その枠の最初の空きセル**。空きが無ければ 0
 * - 省略 —— **ドロップと同じ規則**(`pickCell` に位置なしで渡す: order 順の空きセル、すべて埋まっていれば先頭の枠)
 */
export function resolveAssignTarget(
  frames: readonly PublicVideoFrame[],
  target: AssignTarget | undefined,
  assigned: ReadonlySet<string>,
): Resolved<CellRef> {
  if (target === undefined) {
    const picked = pickCell(null, frames, assigned, () => null);
    return picked === null ? notFound : { ok: true, value: picked };
  }
  if ('externalKey' in target) return findByExternalKey(frames, target.externalKey);

  const frame = frames.find((f) => f.id === target.frameId);
  if (frame === undefined) return notFound;
  if (target.cellIndex !== undefined) {
    return inRange(frame, target.cellIndex)
      ? { ok: true, value: { frameId: frame.id, cellIndex: target.cellIndex } }
      : notFound;
  }
  // **入れ物の数で空きを探す**(#339)。折れた枠はセル 0 の 1 つ。
  const count = slotCountOf(frame);
  for (let cellIndex = 0; cellIndex < count; cellIndex += 1) {
    if (!assigned.has(cellKey({ frameId: frame.id, cellIndex }))) {
      return { ok: true, value: { frameId: frame.id, cellIndex } };
    }
  }
  return { ok: true, value: { frameId: frame.id, cellIndex: 0 } };
}

/** 枠のすべてのセル。 */
function cellsOf(frame: PublicVideoFrame): CellRef[] {
  return Array.from({ length: cellCountOf(frame.grid) }, (_, cellIndex) => ({
    frameId: frame.id,
    cellIndex,
  }));
}

/**
 * `clear` で空にするセル(連動先を引く前)。
 *
 * - 省略 —— **すべて**
 * - `{ frameId }` —— **その枠のすべてのセル**
 * - `{ frameId, cellIndex }` / `{ externalKey }` —— そのセル(見つからなければ `assign` と同じコード)
 */
export function resolveClearTargets(
  frames: readonly PublicVideoFrame[],
  target: AssignTarget | undefined,
): Resolved<readonly CellRef[]> {
  if (target === undefined) return { ok: true, value: frames.flatMap(cellsOf) };
  if ('externalKey' in target) {
    const found = findByExternalKey(frames, target.externalKey);
    return found.ok ? { ok: true, value: [found.value] } : found;
  }
  const frame = frames.find((f) => f.id === target.frameId);
  if (frame === undefined) return notFound;
  if (target.cellIndex === undefined) return { ok: true, value: cellsOf(frame) };
  return inRange(frame, target.cellIndex)
    ? { ok: true, value: [{ frameId: frame.id, cellIndex: target.cellIndex }] }
    : notFound;
}

/**
 * 素材の種別(同意書 Q5)。**明示 → 入れる先の枠の `sourceKind` → 動画**の順。
 *
 * 明示は `{ url, kind }` の `kind`、または `Blob` の `type`(と `File` の名前の拡張子)。
 */
export function resolveSourceKind(
  explicit: SourceKind | null | undefined,
  frameKind: SourceKind | null | undefined,
): SourceKind {
  return explicit ?? frameKind ?? 'video';
}

/** `Blob` の型から読める種別。**読めなければ `null`**(`type` が空など —— 枠の種別に落とす)。 */
export function blobKindOf(blob: Blob): SourceKind | null {
  const name = blob instanceof File ? blob.name : '';
  return sourceKindOf({ name, type: blob.type });
}

/**
 * `Blob` の**同一性の鍵**(ADR-0048 の決定 3)。
 *
 * **同じオブジェクトなら同じ鍵、別のオブジェクトなら別の鍵。** ドロップ用の `fileKeyOf`
 * (名前・サイズ・更新日時・MIME)は、名前の無い `Blob` だと**サイズと MIME が同じだけの
 * 別の素材を同じとみなす**(片方の枠に別の素材が映る)。
 *
 * **`WeakMap` なので、`Blob` を手放せば鍵も消える**(埋め込み先が持ち続けない限り溜まらない)。
 */
const identities = new WeakMap<Blob, string>();
let nextIdentity = 1;

export function identityKeyOf(blob: Blob): string {
  const known = identities.get(blob);
  if (known !== undefined) return known;
  const key = `blob#${String(nextIdentity)}`;
  nextIdentity += 1;
  identities.set(blob, key);
  return key;
}

/** URL として受け付けるスキーム。 */
const URL_PROTOCOLS = new Set(['http:', 'https:', 'blob:', 'data:']);

/**
 * 素材の URL を**絶対 URL にそろえる**(`./a.mp4` と `https://host/a.mp4` を同じ素材にする)。
 * 読めない URL・受け付けないスキームは `null`(`SOURCE_LOAD_FAILED`)。
 */
export function sourceUrlOf(url: string, base: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url, base);
  } catch {
    return null;
  }
  return URL_PROTOCOLS.has(parsed.protocol) ? parsed.href : null;
}

/** URL の共有の鍵(ADR-0048 の決定 3)。**`Blob` の鍵(`blob#n`)と混ざらないよう接頭辞を付ける。** */
export function urlKeyOf(url: string, base: string): string | null {
  const href = sourceUrlOf(url, base);
  return href === null ? null : `url:${href}`;
}

/** `assign` の第 2 引数として受け付ける形か。 */
export function isAssignSource(value: unknown): value is AssignSource {
  if (value instanceof Blob) return true;
  if (typeof value !== 'object' || value === null) return false;
  const { url, kind } = value as { url?: unknown; kind?: unknown };
  return typeof url === 'string' && (kind === undefined || kind === 'video' || kind === 'image');
}

/** `assign` / `clear` の第 3 引数として受け付ける形か。**省略(`undefined`)も可。** */
export function isAssignTarget(value: unknown): value is AssignTarget | undefined {
  if (value === undefined) return true;
  if (typeof value !== 'object' || value === null) return false;
  const { externalKey, frameId, cellIndex } = value as {
    externalKey?: unknown;
    frameId?: unknown;
    cellIndex?: unknown;
  };
  if (typeof externalKey === 'string') return frameId === undefined && cellIndex === undefined;
  return (
    externalKey === undefined &&
    typeof frameId === 'string' &&
    (cellIndex === undefined || typeof cellIndex === 'number')
  );
}

/** `signa:ready` の `detail.frames` の 1 件。 */
export interface ReadyFrame {
  readonly id: string;
  readonly order: number;
  /**
   * **素材の入れ物の数。** 平面の枠はセルの数、**折れた枠は 1**(#339 / ADR-0057 —— 素材は全体で 1 本)。
   * `cells` の外部キーのセル番号は分割のままなので、折れた枠では `cellIndex` がこれ以上になることがある
   * (どのキーで `assign` してもセル 0 に入る)。
   */
  readonly cellCount: number;
  /** 連動番号。**無ければ `null`**。 */
  readonly linkId: number | null;
  /** セルの外部キー(#282)。**無ければ空の配列**。 */
  readonly cells: readonly { readonly cellIndex: number; readonly externalKey: string }[];
}

/**
 * `signa:ready` の枠の一覧(#284)。**埋め込み先が枠と盤面の照合(不足・重複の表示)に使う。**
 *
 * 公開テンプレートには非表示の枠が入っていないので、ここにも出ない。
 */
export function readyFrames(frames: readonly PublicVideoFrame[]): readonly ReadyFrame[] {
  return frames.map((frame) => ({
    id: frame.id,
    order: frame.order,
    // **入れ物の数**(#339)。折れた枠は 1 —— `assignedCount` と同じ単位(全部埋まったかを数で判定できる)。
    cellCount: slotCountOf(frame),
    linkId: frame.linkId ?? null,
    cells: (frame.cells ?? []).map(({ cellIndex, externalKey }) => ({ cellIndex, externalKey })),
  }));
}

/** 割り当てが失敗した理由(シェルの `place` が返す)。 */
export type PlaceFailure =
  'destroyed' | 'superseded' | 'shared-gone' | 'load-failed' | 'too-large' | 'busy';

/** 素材の読み込みの待ちが明けたときの状態(#284 の最終レビュー)。 */
export interface PlacementState {
  /** シェルが破棄された。 */
  readonly destroyed: boolean;
  /** 自分がどのセルでもまだ最後の要求か(後勝ち —— ADR-0048 の決定 2)。 */
  readonly latest: boolean;
  /** 使おうとした要素が、待っている間に捨てられた。 */
  readonly retired: boolean;
  /**
   * 受け入れの待ちが成功で明けた。**動画は 1 枚目まで**(#341 —— `waitForSourceReady`)。
   * 1 枚目を待ちきれずに上限を超えたときは、寸法まで読めた時点で `true`。
   */
  readonly loaded: boolean;
  /** 読めたが幅か高さが 0(音声など —— 描けない)。 */
  readonly empty: boolean;
  /** GPU のテクスチャの上限を超える。 */
  readonly tooLarge: boolean;
  /** 書き出しの最中で、入れてはいけない(`assign` だけ)。 */
  readonly busy: boolean;
}

/**
 * 待ちが明けたときに**入れてよいか**。入れてよければ `null`、だめなら理由。
 *
 * ## 判定の順が意味を持つ(#284 の最終レビューで直した)
 *
 * 1. **破棄** —— 何より先。破棄の後に読み込みが失敗しても、`NOT_READY` で返す
 * 2. **後の要求に負けた** —— **共有元の消失より先。** 先に「消えた」を返すと呼び出し側が
 *    作り直し、その間に来た `clear` / `assign` に負けていたことが消える(後勝ちが破れる)
 * 3. **共有元が消えた** —— 読み込みの失敗より先(捨てられた要素は `emptied` で「読めなかった」に見える)
 * 4. **読めない・幅か高さが 0** —— 音声は `<video>` として読めてしまうので、寸法で弾く
 * 5. **大きすぎる**
 * 6. **書き出しの最中**
 */
export function placementFailure(state: PlacementState): PlaceFailure | null {
  if (state.destroyed) return 'destroyed';
  if (!state.latest) return 'superseded';
  if (state.retired) return 'shared-gone';
  if (!state.loaded || state.empty) return 'load-failed';
  if (state.tooLarge) return 'too-large';
  if (state.busy) return 'busy';
  return null;
}

/** `place` の失敗 → `assign` の reject のコード(ADR-0048)。 */
export function assignCodeOf(reason: PlaceFailure, fromUrl: boolean): AssignErrorCode {
  switch (reason) {
    // **URL は通信・CORS・形式を区別しない**(ADR-0048 の決定 5)。
    case 'load-failed':
      return fromUrl ? ASSIGN_ERROR_CODES.SOURCE_LOAD_FAILED : ASSIGN_ERROR_CODES.VIDEO_UNSUPPORTED;
    case 'too-large':
      return ASSIGN_ERROR_CODES.SOURCE_TOO_LARGE;
    case 'destroyed':
      return ASSIGN_ERROR_CODES.NOT_READY;
    case 'busy':
      return ASSIGN_ERROR_CODES.BUSY;
    // 作り直しても共有元が消えた —— 状態が下で変わり続けている。後の操作に負けたのと同じ扱い。
    case 'shared-gone':
    case 'superseded':
      return ASSIGN_ERROR_CODES.SUPERSEDED;
  }
}

/**
 * **型から対応しないと分かる `Blob`**(音声・SVG など)。読み込む前に `VIDEO_UNSUPPORTED` で弾く。
 *
 * `blobKindOf` は「型が分からない」と「対応しない」をどちらも `null` で返す。型が無いだけなら
 * 枠の種別に落としてよいが、**音声を動画として読むと寸法 0 のまま入り、何も映らない**
 * (`sourceKindOf` が防いでいる壊れ方)。
 */
export function isUnsupportedBlob(blob: Blob): boolean {
  return blob.type !== '' && blobKindOf(blob) === null;
}

/** URL の素材の読み込みを待つ上限(ミリ秒)。**通信が止まった `assign` をいつまでも終わらせないため。** */
export const URL_LOAD_TIMEOUT_MS = 30_000;
